import { prepareLazyComponentsForSource } from '@Pages/pageRuntime/components/registry';
import { prepareSdkDndForSource } from '@Pages/pageRuntime/dnd/lazyDnd';
import { prepareLazyHooksForSource } from '@Pages/pageRuntime/hooks/lazyFormHooks';

/**
 * Runtime that source factories can require while they are evaluated.
 *
 * Components and hooks expose lazy facades that can wait under React Suspense
 * when the authored component renders. `Sdk.Dnd` is a proxy, so a top-level
 * property read needs its implementation before the factory runs.
 */
export function evaluationRuntimeLoads(
  sources: Array<string | null | undefined>
): Promise<unknown> | null {
  return prepareSdkDndForSource(sources);
}

/**
 * The optional runtime chunks these sources reference and have not loaded yet,
 * or null when everything they need is already resident.
 */
export function optionalRuntimeLoads(
  sources: Array<string | null | undefined>
): Promise<unknown> | null {
  const loads = [
    prepareSdkDndForSource(sources),
    prepareLazyHooksForSource(sources),
    prepareLazyComponentsForSource(sources)
  ].filter((load): load is Promise<unknown> => load !== null);

  return loads.length > 0 ? Promise.all(loads) : null;
}

/** Suspends until the optional runtime these sources reference is resident. */
export function prepareOptionalRuntime(sources: Array<string | null | undefined>): void {
  const load = optionalRuntimeLoads(sources);

  if (load !== null) {
    throw load;
  }
}
